import { SimulatedDebt, SimulatedPaymentOption } from '@common/apis/simulated-debt/types';
import { currencyToLocale } from '@libs/utils/helpers/currencyToLocale';
import constants from '@common/constants';
import { SortingDebtResult } from '@common/apis/sorting-debt/types';
import { formatPercentage } from '@dues/pages/Partner/Confirmation/components/DebtComposition/hooks/useFormattedCetProps';
import { useApiPaymentOptions } from '@common/apis/payment-options/useApiPaymentOptions';

type LisAndCard = {
  descriptionLimit: string;
  previousLimit: number;
  promiseLimit: number;
  paymentLimit: number;
};

type CET = {
  monthlyEffectiveCostPercentage: number;
  annualEffectiveCostPercentage: number;
  iofAmount: string;
  iofPercentage: number;
  iofDescription: string;
};

type FinancialDetails = {
  monthlyInterestRate: number;
  annualInterestRate: number;
  overdueInstallmentValue: number;
  cet: CET;
};

type SummaryType = 'in_credit' | 'in_cash';

type Contract = {
  contractId: string;
  productCode: string;
  productName: string;
  lisAndCard?: LisAndCard;
  annualInterestRate?: number;
  monthlyInterestRate?: number;
  totalAmountWithDiscount?: number;
  weightedDiscountPercentage?: number;
};

export type BankAccounts = {
  bank: string;
  agency: string;
  account: string;
};

export type PaymentMethodType = 'pix' | 'boleto' | 'débito em conta';

type PaymentMethod = {
  firstInstallment: {
    type: PaymentMethodType;
    selected: boolean;
  }[];
  otherInstallments: PaymentMethodType;
};
export interface OfferSummaryResponse {
  debtId: number;
  paymentOptionId: number;
  qtyInstallments: number;
  summaryType: SummaryType;
  contracts: Contract[];
  financialDetails?: FinancialDetails;
  bankAccounts?: BankAccounts[];
  paymentMethod: PaymentMethod;
}

export const ITAU_PAYMENT_METHODS = {
  pix: 'PIX',
  boleto: 'BILLET',
  'débito em conta': 'DEBIT_ACCOUNT',
} as const;

const { ITAU_NEGOTIATION_TYPE, ITAU_NEGOTIATION_TYPE_VALUE } = constants.debts.PROPERTIES;

export function getPaymentOptionSummary(
  option: SimulatedPaymentOption,
  debt: SimulatedDebt,
  apiOfferSummaryItau: ReturnType<typeof useApiPaymentOptions>['apiOfferSummaryItau'],
  handleDebtUpdate: (debt: SimulatedDebt) => void
) {
  const payload = {
    debtId: debt?.id,
    paymentOptionId: option?.id,
  };

  const summary = apiOfferSummaryItau
    .send(payload)
    .then((res) => {
      const updateDebt = {
        ...debt,
        summary: res,
        ...(!debt?.hasSelectedPaymentOption && { hasSelectedPaymentOption: true }),
      };

      handleDebtUpdate(updateDebt);
    })
    .catch((error) => console.log(error));
  return summary;
}

export function getLisDebtDetails(summary?: OfferSummaryResponse) {
  const { descriptionLimit, promiseLimit, previousLimit, paymentLimit } = summary?.contracts?.[0].lisAndCard || {};

  const lisProductDetailsList = [
    {
      ...(previousLimit !== undefined && {
        title: 'Limite atual',
        value: currencyToLocale(previousLimit),
      }),
    },
    {
      ...(promiseLimit !== undefined && {
        title: 'Limite na contratação',
        value: currencyToLocale(promiseLimit),
      }),
    },
    {
      ...(paymentLimit !== undefined && {
        title: 'Limite após pag. da 1ª parcela',
        value: currencyToLocale(paymentLimit),
      }),
    },
    {
      ...(descriptionLimit && {
        title: 'Nova situação do limite',
        value: descriptionLimit,
      }),
    },
  ];
  return lisProductDetailsList;
}

export function checkPRAItau(debt?: SimulatedDebt) {
  const isItau = debt?.partner?.identifier.includes('itau');
  if (!isItau) return { hasPRAItau: false };

  const PRAItau = debt?.opcaoPagamento?.some(({ propriedades }) => {
    const optType = propriedades?.find(({ chave }) => chave === ITAU_NEGOTIATION_TYPE);

    return optType && optType?.valorString === ITAU_NEGOTIATION_TYPE_VALUE;
  });

  const PRAItauOptions = debt?.opcaoPagamento?.filter(({ propriedades }) => {
    const optType = propriedades?.find(({ chave }) => chave === constants.debts.PROPERTIES.ITAU_NEGOTIATION_TYPE);
    return optType && optType?.valorString === ITAU_NEGOTIATION_TYPE_VALUE;
  });

  const PRAItauUpcomingOptions = debt?.opcaoPagamento?.filter(({ propriedades }) => {
    const optType = propriedades?.find(
      ({ chave, valorString }) => chave === constants.debts.PROPERTIES.ITAU_NEGOTIATION_TYPE && valorString === 'OVERDUE'
    );
    return !optType;
  });

  return {
    hasPRAItau: PRAItau,
    optionsOverdueOnlyItau: PRAItauOptions,
    optionsOverdueUpcomingItau: PRAItauUpcomingOptions,
  };
}

export function shouldPreferSemPRAItau(
  optionsOverdueOnlyItau: SimulatedPaymentOption[],
  optionsOverdueUpcomingItau: SimulatedPaymentOption[]
): boolean {
  const comPRAValue = optionsOverdueUpcomingItau?.[0]?.valorAcordo;
  const semPRAValue = optionsOverdueOnlyItau?.[0]?.valorAcordo;
  if (!comPRAValue || !semPRAValue) return false;
  if (comPRAValue <= 500) return false;
  if (!optionsOverdueOnlyItau.some((opt) => opt.qtdParcela > 1)) return false;
  if (semPRAValue > 2 * comPRAValue) return false;
  return true;
}

export function getPRAPaymentOption(debt: SortingDebtResult) {
  const withPraItau = debt?.paymentOptions?.find(({ paymentOptionProperties }) => {
    const optType = paymentOptionProperties?.find(
      ({ key, stringValue }) => key === ITAU_NEGOTIATION_TYPE && stringValue === ITAU_NEGOTIATION_TYPE_VALUE
    );

    return optType;
  });

  return withPraItau;
}

export function hasPRAType(debt: SimulatedDebt) {
  return debt?.typeSelected?.includes('overdued_and_upcoming') || debt?.typeSelected?.includes('overdued_only');
}

export function hasCreditCardDisclaimer(debt: SimulatedDebt) {
  const props = debt?.propriedades || [];
  return props.some((p) => p.chave === 'CLASSIFICATION_PRODUCT_TYPE_CODE' && p.valorString === 'CC');
}

export function isDesenrolaItau(debt?: SimulatedDebt): boolean {
  const isItau = !!debt?.partner?.identifier?.includes('itau');
  const isDesenrola = !!debt?.propriedades?.some(
    (prop) => prop.chave === 'IS_DESENROLA' && prop.valorBoolean === true
  );
  return isItau && isDesenrola;
}

export function getCETProps(debt: SimulatedDebt) {
  const { cet, annualInterestRate, overdueInstallmentValue, monthlyInterestRate } = debt?.summary?.financialDetails || {};

  if (!cet) return;
  // TODO: check cet.iofDescription return
  const itauCET = [
    {
      ...(cet?.monthlyEffectiveCostPercentage !== undefined && {
        title: 'CET mensal:',
        value: formatPercentage(cet.monthlyEffectiveCostPercentage),
      }),
    },
    {
      ...(cet?.annualEffectiveCostPercentage !== undefined && {
        title: 'CET anual:',
        value: formatPercentage(cet.annualEffectiveCostPercentage),
      }),
    },
    {
      ...(monthlyInterestRate !== undefined && {
        title: 'Juros mensal:',
        value: formatPercentage(monthlyInterestRate),
      }),
    },
    {
      ...(annualInterestRate !== undefined && {
        title: 'Juros anual:',
        value: formatPercentage(annualInterestRate),
      }),
    },
    {
      ...(overdueInstallmentValue !== undefined && {
        title: 'Valor após vencimento:',
        value: currencyToLocale(overdueInstallmentValue),
      }),
    },
    {
      ...(cet?.iofAmount !== undefined && {
        title: 'IOF:',
        value: currencyToLocale(Number(cet.iofAmount)),
      }),
    },
    {
      ...(cet?.iofPercentage !== undefined && {
        title: 'Percentual do valor do IOF:',
        value: formatPercentage(cet.iofPercentage),
      }),
    },
  ];
  return itauCET;
}

export function getContractDetails(debt: SimulatedDebt) {
  const { annualInterestRate, monthlyInterestRate, weightedDiscountPercentage, totalAmountWithDiscount } = debt?.summary?.contracts?.[0] || {};

  const detailedContractInfo = {
    ...(monthlyInterestRate !== undefined && {
      jurosMensal: formatPercentage(monthlyInterestRate),
    }),
    ...(annualInterestRate !== undefined && {
      jurosAnual: formatPercentage(annualInterestRate),
    }),
    ...(totalAmountWithDiscount !== undefined && {
      valorAcordo: currencyToLocale(totalAmountWithDiscount),
    }),
    ...(weightedDiscountPercentage !== undefined && {
      percentualDesconto: Math.floor(weightedDiscountPercentage),
    }),
  };

  const details = debt?.detalhes?.[0];
  return [{ ...details, ...detailedContractInfo }];
}
